import { useRef } from 'react';
import { gsap, useGSAP } from './gsap';

/**
 * Counts a figure up to its final value when it scrolls into view.
 *
 * `value` is the literal label ("500+", "1", "Free"). Anything without digits
 * is left alone, so a mixed set of figures can share one hook.
 */
export function useCountUp<T extends HTMLElement>(value: string) {
  const ref = useRef<T>(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;

      const match = value.match(/^(\D*)(\d[\d,]*)(.*)$/);
      if (!match) return;

      const [, prefix, digits, suffix] = match;
      const target = Number(digits.replace(/,/g, ''));
      const counter = { n: 0 };

      const mm = gsap.matchMedia();

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        gsap.to(counter, {
          n: target,
          duration: 1.6,
          ease: 'power2.out',
          snap: { n: 1 },
          onUpdate: () => {
            el.textContent = `${prefix}${Math.round(counter.n).toLocaleString()}${suffix}`;
          },
          scrollTrigger: { trigger: el, start: 'top 85%', once: true },
        });
        // Start from zero so the count is visible from its first frame.
        el.textContent = `${prefix}0${suffix}`;
      });

      return () => mm.revert();
    },
    { scope: ref, dependencies: [value] },
  );

  return ref;
}
